/**
 * Publically accessible data store.
 * Triggers callbacks when values are updated.
 * Core of key data and messaging systems.
 * @module PropertySwitchboard
 * @class PropertySwitchboard
 */
const PropertySwitchboard = class {
    constructor () {
        /**
         * Callbacks to invoke when a property updates
         * @type {Array}
         * @private
         */
        this._callbacks = [];

        /**
         * Values stored in the switchboard.
         * @type {Array}
         * @private
         */
        this._properties = [];
    }

    /**
     * Remove all callbacks and properties from the switchboard.
     */
    clear () {
        this._callbacks = [];
        this._properties = [];
    }

    /**
     * Return the value of a given key.
     * @param {String} name - key to get
     * @return {any}
     */
    get ( name ) {
        return this._properties[ name ];
    }

    /**
     * Return whether a given key exists.
     * @param {String} name - key to verify
     * @return {Boolean}
     */
    has ( name ) {
        return !!this.get( name );
    }

    /**
     * Set a key-value pair, and trigger any associated events.
     * @param {String} name - key to set
     * @param {any} value - value to associate with key
     */
    set ( name, value ) {
        this._properties[ name ] = value;
        this._executeCallbacks( name, value );
    }

    /**
     * Unset a key.
     * @param {String} name - key to unset
     */
    delete ( name ) {
        delete this._properties[ name ];
        this._executeCallbacks( name, null );
    }

    /**
     * Run callbacks associated with a given key.
     * @param {String} name - key that was modified
     * @param {any} value - value of the key
     * @private
     */
    _executeCallbacks ( name, value ) {
        this._callbacks.forEach( callback => {
            if ( !callback.name || callback.name === name ) {
                callback.callback.call( callback.context, name, value );
            }
        } );
    }

    /**
     * Associate a callback with a key.
     * @param {Function} callback - method to call when the key updates
     * @param {Any} context - scope in which to execute callback
     * @param {String} [name] - key to associate with the callback
     */
    onUpdate ( callback, context, name ) {
        if ( !callback ) {
            console.error(
                "PropertySwitchboard: No callback was passed. Skipping.",
                "#properties" );
            return;
        }

        name = name || null;

        this._callbacks.push( {
            callback: callback,
            context: context,
            name: name,
        } );
    }

    /**
     * Remove a callback from a key.
     * Note that this will only remove one instance of the callback.
     * @param {Function} callback - method associated with the key
     * @param {Any} context - callback scope
     * @param {String} [name] - key to update
     * @return {Boolean} Whether the removal occurred
     */
    removeOnUpdate ( callback, context, name ) {
        name = name || null;
        for ( let i = this._callbacks.length - 1; i >= 0; i-- ) {
            let current = this._callbacks[ i ];
            if (
                current.callback === callback &&
                current.context === context &&
                current.name === name ) {
                
                this._callbacks.splice( i, 1 );
                return true;
            }
        }
        return false;
    }
};

export default PropertySwitchboard;
